export const BANQUET_OWNER_ROLES = {
  SUPER_ADMIN: "super_admin",
  BANQUET_OWNER: "banquet_owner",
  BANQUET_MANAGER: "banquet_manager",
  BANQUET_STAFF: "banquet_staff",
};

export const VALID_BANQUET_OWNER_ROLES = Object.values(BANQUET_OWNER_ROLES);

export const createEmptyBanquetOwner = () => ({
  uid: "",
  fullName: "",
  name: "",
  displayName: "",
  email: "",
  phone: "",
  role: BANQUET_OWNER_ROLES.BANQUET_OWNER,
  assignedBanquetIds: [],
  primaryBanquetId: "",
  isActive: true,
  active: true,
  createdAt: null,
  updatedAt: null,
  createdBy: "",
  notes: "",
});

const assignedBanquetValueToIds = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(assignedBanquetValueToIds);
  }

  if (typeof value === "string") {
    return value.split(/[\n,]/);
  }

  if (typeof value === "object") {
    const directId =
      value.id ||
      value.banquetId ||
      value.banquet_id ||
      value.assignedBanquetId ||
      value.assignedBanquetID ||
      value.value ||
      value.docId ||
      value.slug;

    if (directId) {
      return [directId];
    }

    return Object.entries(value)
      .filter(([, enabled]) => enabled === true)
      .map(([banquetId]) => banquetId);
  }

  return [];
};

export const normalizeAssignedBanquetIds = (owner = {}) => {
  const rawValues = [
    owner.assignedBanquetIds,
    owner["assignedBanquetIds[]"],
    owner.assignedBanquetId,
    owner.banquetIds,
    owner.assignedBanquets,
  ];

  return [
    ...new Set(
      rawValues
        .flatMap(assignedBanquetValueToIds)
        .map((item) => String(item).trim())
        .filter(Boolean)
    ),
  ];
};

export const normalizeBanquetOwnerRole = (role) =>
  VALID_BANQUET_OWNER_ROLES.includes(role) ? role : BANQUET_OWNER_ROLES.BANQUET_OWNER;

export const normalizeBanquetOwner = (owner = {}) => {
  const base = createEmptyBanquetOwner();
  const assignedBanquetIds = normalizeAssignedBanquetIds(owner);
  const fullName = owner.fullName || owner.displayName || owner.name || base.fullName;
  const name = owner.name || owner.fullName || owner.displayName || base.name;
  const displayName = owner.displayName || owner.fullName || owner.name || base.displayName;

  return {
    ...base,
    ...owner,
    uid: owner.uid || base.uid,
    fullName,
    name,
    displayName,
    email: owner.email || base.email,
    phone: owner.phone || base.phone,
    role: normalizeBanquetOwnerRole(owner.role || base.role),
    assignedBanquetIds,
    primaryBanquetId: owner.primaryBanquetId || assignedBanquetIds[0] || base.primaryBanquetId,
    isActive: typeof owner.isActive === "boolean" ? owner.isActive : typeof owner.active === "boolean" ? owner.active : base.isActive,
    active: typeof owner.active === "boolean" ? owner.active : typeof owner.isActive === "boolean" ? owner.isActive : base.active,
    createdAt: owner.createdAt ?? base.createdAt,
    updatedAt: owner.updatedAt ?? base.updatedAt,
    createdBy: owner.createdBy || base.createdBy,
    notes: owner.notes || base.notes,
  };
};

export const buildRequiredBanquetOwnerProfileFields = (owner = {}) => {
  const normalized = normalizeBanquetOwner(owner);
  const fallbackName =
    normalized.fullName ||
    normalized.displayName ||
    normalized.name ||
    String(normalized.email || "").split("@")[0] ||
    "";

  return {
    uid: normalized.uid,
    fullName: normalized.fullName || fallbackName,
    name: normalized.name || fallbackName,
    displayName: normalized.displayName || fallbackName,
    email: normalized.email,
    phone: normalized.phone,
    role: normalized.role,
    assignedBanquetIds: normalized.assignedBanquetIds,
    primaryBanquetId: normalized.primaryBanquetId,
    isActive: normalized.isActive,
    active: normalized.active,
    createdBy: normalized.createdBy,
    notes: normalized.notes,
  };
};

export const banquetOwnerToFirestore = (owner = {}) => {
  const normalized = {
    ...normalizeBanquetOwner(owner),
    ...buildRequiredBanquetOwnerProfileFields(owner),
  };

  return {
    uid: normalized.uid,
    fullName: normalized.fullName,
    name: normalized.name,
    displayName: normalized.displayName,
    email: normalized.email,
    phone: normalized.phone,
    role: normalized.role,
    assignedBanquetIds: normalized.assignedBanquetIds,
    primaryBanquetId: normalized.primaryBanquetId,
    isActive: normalized.isActive,
    active: normalized.active,
    createdAt: normalized.createdAt,
    updatedAt: normalized.updatedAt,
    createdBy: normalized.createdBy,
    notes: normalized.notes,
  };
};

export const isSuperAdminRole = (role) => role === BANQUET_OWNER_ROLES.SUPER_ADMIN;
export const isBanquetOwnerRole = (role) => role === BANQUET_OWNER_ROLES.BANQUET_OWNER;
export const isBanquetManagerRole = (role) => role === BANQUET_OWNER_ROLES.BANQUET_MANAGER;
export const isBanquetStaffRole = (role) => role === BANQUET_OWNER_ROLES.BANQUET_STAFF;
export const isBanquetUserRole = (role) =>
  [BANQUET_OWNER_ROLES.BANQUET_OWNER, BANQUET_OWNER_ROLES.BANQUET_MANAGER, BANQUET_OWNER_ROLES.BANQUET_STAFF].includes(role);
